import React, { useEffect, useRef, useState } from "react";
import DialogWrapper from "../../custom-ui/dialog/Dialog";
import CrossSVG from "../../custom-ui/icons/CrossSVG";
import toast from "react-hot-toast";

declare global {
  interface Window {
    EBWidgets?: {
      createWidget: (config: Record<string, unknown>) => void;
    };
  }
}

const EB_WIDGETS_SCRIPT = "https://www.eventbrite.com/static/widgets/eb_widgets.js";
const MODAL_VIEWPORT_RATIO = 0.88;
const HEADER_RESERVED_PX = 88;

function getCheckoutIframeHeight() {
  const available = Math.floor(window.innerHeight * MODAL_VIEWPORT_RATIO - HEADER_RESERVED_PX);
  return Math.max(420, Math.min(560, available));
}

interface EventbriteCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventbriteId: string;
}

function loadEventbriteWidgetsScript(): Promise<void> {
  if (window.EBWidgets) {
    return Promise.resolve();
  }

  const existing = document.querySelector(`script[src="${EB_WIDGETS_SCRIPT}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Eventbrite widget")), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = EB_WIDGETS_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Eventbrite widget"));
    document.body.appendChild(script);
  });
}

export default function EventbriteCheckoutModal({
  isOpen,
  onClose,
  eventbriteId,
}: EventbriteCheckoutModalProps) {
  const containerIdRef = useRef(`eventbrite-checkout-${eventbriteId}`);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(getCheckoutIframeHeight);

  useEffect(() => {
    if (!isOpen) return;

    const updateHeight = () => setIframeHeight(getCheckoutIframeHeight());
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !eventbriteId) return;

    let cancelled = false;
    setIsLoading(true);
    setHasError(false);
    setIsComplete(false);

    const container = document.getElementById(containerIdRef.current);
    if (container) {
      container.innerHTML = "";
    }

    const height = getCheckoutIframeHeight();

    loadEventbriteWidgetsScript()
      .then(() => {
        if (cancelled || !window.EBWidgets) return;

        window.EBWidgets.createWidget({
          widgetType: "checkout",
          eventId: eventbriteId,
          iframeContainerId: containerIdRef.current,
          iframeContainerHeight: height,
          onOrderComplete: () => {
            const container = document.getElementById(containerIdRef.current);
            if (container) {
              container.innerHTML = "";
            }
            setIsComplete(true);
            toast.success("Registration completed successfully!");
          },
        });

        if (!cancelled) {
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, eventbriteId]);

  useEffect(() => {
    if (!isComplete) return;

    const container = document.getElementById(containerIdRef.current);
    if (container) {
      container.innerHTML = "";
    }
  }, [isComplete]);

  if (!isOpen) return null;

  return (
    <DialogWrapper
      title=""
      size={isComplete ? "w-[90%] max-w-lg" : "w-[90%] max-w-3xl"}
      onClose={onClose}
      displayCloseIcon={true}
      preventScroll={true}
      contentClassName="!p-4 !pt-2 overflow-hidden"
    >
      <div className={`flex flex-col overflow-hidden ${isComplete ? "" : "max-h-[calc(88vh-16px)]"}`}>
        <div className="flex items-center justify-between border-b-2 border-instant-teams-green-D2 pb-2 mb-4 shrink-0">
          <h2 className="text-[#1a3b5d] text-xl font-bold leading-tight">
            {isComplete ? "Registration complete" : "Register for event"}
          </h2>
          <button
            type="button"
            className="w-[36px] h-[36px] rounded-full bg-[#F8F9FA] text-[#183852] focus:outline-none hover:bg-slate-200 flex flex-row justify-center items-center shrink-0"
            onClick={onClose}
          >
            <CrossSVG />
          </button>
        </div>

        {isComplete ? (
          <div className="py-4 text-center">
            <p className="text-gray-700 mb-6">
              Your registration was submitted successfully. You should receive a confirmation from Eventbrite shortly.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="bg-instant-teams-blue-D1 hover:bg-gray-900 text-white font-semibold py-3 px-6 rounded-xl"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {isLoading && (
              <p className="text-sm text-gray-500 mb-1 shrink-0">Loading registration form...</p>
            )}
            {hasError && (
              <p className="text-sm text-red-600 mb-1 shrink-0">
                Unable to load the registration form. Please try again later.
              </p>
            )}
            <div
              id={containerIdRef.current}
              className="w-full shrink-0 overflow-hidden"
              style={{ height: iframeHeight }}
            />
          </>
        )}
      </div>
    </DialogWrapper>
  );
}
